import { Localized, useLocalization } from "@fluent/react";
import { Alert, AlertTitle, Box, Button, ButtonGroup, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Paper, Skeleton, Stack, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import PrintIcon from '@mui/icons-material/Print';
import IconButton from '@mui/material/IconButton';
import PageviewIcon from '@mui/icons-material/Pageview';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import SendIcon from '@mui/icons-material/Send';
import Divider from '@mui/material/Divider';
import { AppId } from "../../app/appTypes";
import { selectSettings } from '../settings/settingsSlice';
import { useAppDispatch, useAppSelector, useDataGridCustomLocaleText } from "../../app/hooks";
import { loadAllUrsOffers, Offers, selectSupplierOffer, selectSupplierOfferWihtNewVersion, Urs } from "./UrsSlice";
import { useCallback, useEffect, useState } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import { DataGrid, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { ErrorContainer } from "../../app/ErrorContainer";
import { LoadingContainer } from "../../app/LoadingContainer";
import { CancelButton } from "../../app/components/CancelButton";
import { SendButton } from "../../app/components/SendButton";
import { showError, showSuccess } from "../notifications/notificationsSlice";
import { sendFeedbackToSupplier } from "../ursOffer/ursOfferSlice";
import { UrsOfferStatusChip } from "../ursOffer/UrsOfferStatusChip";
import { PubAccessRequest, setStatusOfAccessRequest } from "../documents/publicAccessApi";
import { LoadingButton } from "@mui/lab";
import LoginIcon from '@mui/icons-material/Login';
import BlockIcon from '@mui/icons-material/Block';
import { PublishLink } from "./PublishLink";
import HistoryIcon from '@mui/icons-material/History';
import { parseAndFormatISODate } from "../../app/Utils";

export const UrsOffers = (props: {
    ursId: AppId,
    handleNewVersion: (urs: Urs) => void
    accessRequests: PubAccessRequest[]
    accessRequestDone: (reqId: string) => void
}) => {
    const { ursId, handleNewVersion, accessRequests } = props
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const { locale } = useAppSelector(selectSettings);
    const { l10n } = useLocalization()
    const [ursOffers, setUrsOffers] = useState<Offers[] | null>(null)
    const localeText = useDataGridCustomLocaleText();
    const [saving, setSaving] = useState(false)
    const [selectedOfferId, setSelectedOfferId] = useState<AppId>("")
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);

    const loadOffers = () => {
        setIsLoading(true);
        dispatch(loadAllUrsOffers(ursId))
            .then(unwrapResult)
            .then(setUrsOffers)
            .catch((error) => {
                console.error(error);
                setIsError(true);
                setError(error);
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        loadOffers();
    }, []);

    const [showSelectOfferMessage, setShowSelectOfferMessage] = useState(false);
    const [hasnotUnfulfilledRequirements, setHasnotUnfulfilledRequirements] = useState(false);
    const [showSendToSupplierForCommentingDialog, setShowSendToSupplierForCommentingDialog] = useState(false);
    const [accessRequestsHistory, setAccessRequestsHistory] = useState(false)

    const handleConfirmWithoutNewVersionClick = async () => {
        setSaving(true)
        setShowSelectOfferMessage(false)

        try {
            const result = await dispatch(selectSupplierOffer(selectedOfferId))
            if (selectSupplierOffer.fulfilled.match(result)) {
                dispatch(showSuccess("saved"))
                loadOffers();
            }
            else {
                let errorMsg = "error"
                if (result.payload) {
                    if (result.payload.kind === 'http') {
                        const problem = result.payload.problem
                        if (problem) {
                            errorMsg = problem.title
                        }
                    }
                }
                dispatch(showError(errorMsg))
            }
            dispatch(showSuccess("saved"))
        } catch (error) {
            dispatch(showError("error"))
        }
        setSaving(false)
    }

    const handleCancelSelectOfferClick = useCallback(() => {
        setShowSelectOfferMessage(false)
    }, [setShowSelectOfferMessage])

    const handleSelectOfferClick = async (id: AppId) => {
        if (ursOffers !== null) {
            let result = ursOffers.find(x => x.id === id);
            if (result) {
                if (result.hasUnfunfilledRequirements) {
                    setHasnotUnfulfilledRequirements(result.hasUnfunfilledRequirements);
                }

                setSelectedOfferId(id);
                setShowSelectOfferMessage(true)
            }
        }
    }

    const handleSendToSupplierForCommenting = async (id: AppId) => {
        if (ursOffers !== null) {
            setSelectedOfferId(id);
            setShowSendToSupplierForCommentingDialog(true)
        }
    }

    const handleCancelSendToSupplierForCommentingClick = useCallback(() => {
        setShowSendToSupplierForCommentingDialog(false)
    }, [setShowSendToSupplierForCommentingDialog])

    const handleConfirmNewVersionClick = async () => {
        setSaving(true)
        setShowSelectOfferMessage(false)
        try {
            const result = await dispatch(selectSupplierOfferWihtNewVersion(selectedOfferId))
            if (selectSupplierOfferWihtNewVersion.fulfilled.match(result)) {
                dispatch(showSuccess("saved"))

                const newEntity = unwrapResult(result)
                handleNewVersion(newEntity)
            }
            else {
                let errorMsg = "error"
                if (result.payload) {
                    if (result.payload.kind === 'http') {
                        const problem = result.payload.problem
                        if (problem) {
                            errorMsg = problem.title
                        }
                    }
                }
                dispatch(showError(errorMsg))
            }

        } catch (error) {
            dispatch(showError("error"))
        }
        setSaving(false)
    }

    const handleSendToSupplierForCommentingClick = async () => {
        setSaving(true)

        try {
            const result = await dispatch(sendFeedbackToSupplier(selectedOfferId))
            if (sendFeedbackToSupplier.fulfilled.match(result)) {
                dispatch(showSuccess("saved"))
                setShowSendToSupplierForCommentingDialog(false)
                loadOffers();
            } else {
                let errorMsg = "error"
                if (result.payload && result.payload.kind === 'http') {
                    const problem = result.payload.problem
                    if (problem) {
                        errorMsg = problem.title

                    }
                }
                dispatch(showError(errorMsg))
            }
        } catch (error) {
            dispatch(showError("error"))
        }
        finally {
            setSaving(false)
        }
    }

    const handlePrintUrsClick = async (id: AppId) => {
        if (id) {
            window.open(`${process.env.REACT_APP_REST_API}api/reports/urs-offer/${id}?language=${locale}`, '_blank')
        }
    }

    const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);

    const handleSelectionChange = (selection: GridRowSelectionModel) => {
        setSelectedRows(selection);
    };

    const handleCompare = () => {
        if (selectedRows && selectedRows.length > 0) {
            navigate("/urs-offer-comparison", { state: { selectedOfferIds: selectedRows, ursId: ursId } });
        }
    };

    const handlePrint = async () => {
        const offerIdsParam = selectedRows.map(id => `offerIds=${id}`).join("&");
        if (offerIdsParam) {
            const url = `${process.env.REACT_APP_REST_API}api/urs-offer-comparator/report/${ursId}?language=${locale}&${offerIdsParam}`;
            window.open(url, '_blank');
        }
    };

    const handleAccessRequestsHistoryClick = useCallback(() => {
        setAccessRequestsHistory(true)
    }, [])

    const accessRequestDone = useCallback((requestId: string, approved: boolean) => {
        props.accessRequestDone(requestId)
        if (approved) {
            loadOffers()
        }
    }, [props.accessRequestDone])

    const columns: GridColDef[] = [
        {
            field: 'supplierName',
            headerName: l10n.getString("urs-offer-supplier", null, "_Dostawca"),
            flex: 1
        },
        {
            field: 'ursNo',
            headerName: l10n.getString("document-number", null, "_Document number"),
            minWidth: 150
        },
        {
            field: 'ursVersion',
            headerName: l10n.getString("document-version", null, "_Document version"),
            valueGetter: (_, row) => {
                return row.ursVersion
            }
        },
        {
            field: 'createDate',
            headerName: l10n.getString("urs-offer-send-date", null, "_Data wysłania"),
            minWidth: 200,

            valueGetter: (_, row) => {
                return row.createDate ? new Date(row.createDate).toISOString().split('T')[0] : "";
            }
        },
        {
            field: 'responseDate',
            headerName: l10n.getString("urs-offer-response-date", null, "_Data odpowiedzi"),
            minWidth: 200,

            valueGetter: (_, row) => {
                return row.responseDate ? new Date(row.responseDate).toISOString().split('T')[0] : "";
            }
        },
        {
            field: 'comment',
            headerName: l10n.getString("urs-offer-comment", null, "_Komentarz"),
            minWidth: 200
        },
        {
            field: 'status',
            headerName: l10n.getString("urs-offer-status", null, "_Status"),
            width: 200,
            renderCell: (params) => {
                return (
                    <UrsOfferStatusChip status={params.row.status} />
                );
            }
        },
        {
            field: 'actions', type: 'actions', width: 200, align: 'right',
            renderCell: (params) => {
                return (
                    <ButtonGroup size="small" >
                        <ButtonGroup>
                            <Tooltip title={<Localized id="urs-offer-action-select">_select</Localized>}>
                                <IconButton
                                    onClick={() => handleSelectOfferClick(params.row.id)}
                                    style={{ display: params.row.status === "F" ? "inline-flex" : "none" }}>
                                    <PriceCheckIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={<Localized id="urs-offer-action-send-feedback">_comment</Localized>}>
                                <IconButton
                                    onClick={() => handleSendToSupplierForCommenting(params.row.id)}
                                    style={{ display: params.row.status === "A" ? "inline-flex" : "none" }}>
                                    <SendIcon />
                                </IconButton>
                            </Tooltip>
                            <Divider orientation="vertical" flexItem />
                            <Tooltip title={<Localized id="urs-offer-action-print">_print</Localized>}>
                                <IconButton aria-label="print" onClick={() => handlePrintUrsClick(params.row.id)} style={{ display: params.row.status !== "N" ? "inline-flex" : "none" }}>
                                    <PrintIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={<Localized id="go-to">_goto</Localized>}>
                                <IconButton aria-label="open" component={Link} to={`/offer-preview/${params.row.id}`}>
                                    <PageviewIcon />
                                </IconButton>
                            </Tooltip>
                        </ButtonGroup>
                    </ButtonGroup>
                )
            }
        }
    ]

    if (isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }

    if (ursOffers == null) return <Skeleton />

    return <>
        <Box sx={{
            height: 'calc(100vh - 300px)',
            overflow: 'auto',
        }}>
            {accessRequests.length > 0 && <Stack sx={{ mb: 1 }} gap={1}>
                {accessRequests.filter(a => a.status === 'Pending').map(a => <AccessRequest key={a.requestId} entity={a} done={accessRequestDone} />)}
            </Stack>}
            <DataGrid
                rows={ursOffers}
                columns={columns}
                style={{ minHeight: 100, height: 'calc(100vh - 400px)' }}
                localeText={localeText}
                checkboxSelection
                onRowSelectionModelChange={handleSelectionChange}
                rowSelectionModel={selectedRows}
            />
            {
                <Paper >
                    <Alert severity="info" sx={{ paddingLeft: 1, width: '100%' }} >
                        <AlertTitle sx={{ width: '100%' }}>
                            <>
                                <Localized id="selected-count">Wybrano </Localized>  {selectedRows.length}
                                <Button startIcon={<CompareArrowsIcon />} onClick={handleCompare} variant="contained" color="primary" size="small" sx={{ marginLeft: 5 }}>
                                    <Localized id="compare">_porównaj </Localized> ({selectedRows.length})
                                </Button>
                                <Button startIcon={<PrintIcon />} onClick={handlePrint} variant="contained" color="secondary" size="small" sx={{ marginLeft: 1 }}>
                                    <Localized id="print-compare">_Drukuj </Localized>
                                </Button >
                                <Tooltip title='Historia nadawania dostępu'>
                                    <Button startIcon={<HistoryIcon />} variant='contained' color='primary' size='small' sx={{ ml: 1 }} onClick={handleAccessRequestsHistoryClick}>
                                        <Localized id='history'>Historia</Localized>
                                    </Button>
                                </Tooltip>
                                <PublishLink objectId={ursId} objectType="URS_REQUEST" />
                            </>
                        </AlertTitle>
                    </Alert>
                </Paper>
            }
        </Box >

        <Dialog open={showSelectOfferMessage} >
            <DialogTitle>
                <Localized id="urs-offer-action-approve-question">_Czy zatwierdzić wskazaną ofertę?</Localized>
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {hasnotUnfulfilledRequirements ? (
                        <Alert severity="warning">
                            {l10n.getString(
                                "urs-offer-all-requirements-not-ok-message",
                                null,
                                "Wybrana oferta zawiera wymagania, które nie zostały spełnione przez producenta. Wskazane jest utworzenie nowej wersji URS."
                            )}
                        </Alert>
                    ) : (
                        <Alert severity="info">
                            {l10n.getString(
                                "urs-offer-all-requirements-ok-message",
                                null,
                                "Wybrana oferta spełnia wszystkie wymagania"
                            )}
                        </Alert>
                    )}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleConfirmNewVersionClick}
                    sx={{
                        '&:hover': {
                            backgroundColor: 'success.main',
                            color: 'white',
                            borderColor: 'success.main',
                        },
                    }}
                    variant="outlined" color="primary" >
                    <Localized id="urs-offer-action-approve-new-version">
                        <span>Zatwierdź z nową wersją URS</span>
                    </Localized>
                </Button>
                <Button onClick={handleConfirmWithoutNewVersionClick}
                    sx={{
                        '&:hover': {
                            backgroundColor: 'success.main',
                            color: 'white',
                            borderColor: 'success.main',
                        },
                    }} variant="outlined" >
                    <Localized id="urs-offer-action-approve"><span>Zatwierdź</span></Localized>
                </Button>
                <Button onClick={handleCancelSelectOfferClick} sx={{
                    '&:hover': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderColor: 'primary.main',
                    },
                }} variant="outlined">
                    <Localized id="cancel"><span>Anuluj</span></Localized>
                </Button>
            </DialogActions>
        </Dialog>

        <Dialog open={showSendToSupplierForCommentingDialog}>
            <DialogTitle>
                <Localized id="urs-offer-action-send-for-commenting-title">_send for commenting</Localized>
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    <Localized id="urs-offer-action-send-for-commenting-message">_commenting-message</Localized>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <SendButton loading={saving} onClick={handleSendToSupplierForCommentingClick}></SendButton>
                <CancelButton loading={saving} onClick={handleCancelSendToSupplierForCommentingClick}></CancelButton>
            </DialogActions>
        </Dialog >

        <Dialog open={accessRequestsHistory} fullWidth maxWidth='lg'>
            <DialogTitle>
                <Localized id="urs-offer-access-requests-history">Historia nadawania dostępu publikowanych URS</Localized>
            </DialogTitle>
            <DialogContent>
                <TableContainer component={Paper}>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Localized id='urs-offer-access-requests-history-email'>
                                    Email
                                </Localized>
                            </TableCell>
                            <TableCell>
                                <Localized id='urs-offer-access-requests-history-status'>
                                    Status
                                </Localized>
                            </TableCell>
                            <TableCell>
                                <Localized id='urs-offer-access-requests-history-user'>
                                    Użytkownik zatwierdzający
                                </Localized>
                            </TableCell>
                            <TableCell>
                                <Localized id='urs-offer-access-requests-history-created-at'>
                                    Data utworzenia
                                </Localized>
                            </TableCell>
                            <TableCell>
                                <Localized id='urs-offer-access-requests-history-decision-at'>
                                    Data decyzji
                                </Localized>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {accessRequests.filter(r => r.status !== 'Pending').map(r => <TableRow>
                            <TableCell>{r.email}</TableCell>
                            <TableCell>{r.status}</TableCell>
                            <TableCell>{r.decisionByUser}</TableCell>
                            <TableCell>{parseAndFormatISODate(r.requestedAt)}</TableCell>
                            <TableCell>{parseAndFormatISODate(r.decisionAt ?? '')}</TableCell>
                        </TableRow>)}
                    </TableBody>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setAccessRequestsHistory(false)}>
                    <Localized id='close'>Zamknij</Localized>
                </Button>
            </DialogActions>
        </Dialog >
    </>
}

const AccessRequest = (props: { entity: PubAccessRequest, done: (reqId: string, approved: boolean) => void }) => {
    const { done } = props
    const dispatch = useAppDispatch()

    const [loading, setLoading] = useState(false)

    const handleAllowClick = async () => {
        try {
            setLoading(true)
            await dispatch(setStatusOfAccessRequest({
                requestId: entity.requestId,
                approve: true,
            })).unwrap()
            done(entity.requestId, true)
            dispatch(showSuccess('saved'))
        } catch (error) {
            console.log(error)
            dispatch(showError('error'))
        } finally {
            setLoading(false)
        }
    }

    const handleDenyClick = async () => {
        try {
            setLoading(true)
            await dispatch(setStatusOfAccessRequest({
                requestId: entity.requestId,
                approve: false,
            })).unwrap()
            done(entity.requestId, false)
            dispatch(showSuccess('saved'))
        } catch (error) {
            console.log(error)
            dispatch(showError('error'))
        } finally {
            setLoading(false)
        }
    }

    const { entity } = props
    return <Paper sx={{
        bgcolor: 'var(--gray-100)',
        border: '1px solid var(--gray-400)',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
        p: 1,
    }}>
        <Box>Prośba o udostępnienie dokumentu dla <Typography sx={{ color: 'var(--purple-600)' }} component='span'>{entity.email}</Typography></Box>
        <LoadingButton loading={loading} onClick={handleAllowClick} startIcon={<LoginIcon />} variant='contained' color='success'>Zgoda</LoadingButton>
        <LoadingButton loading={loading} onClick={handleDenyClick} startIcon={<BlockIcon />} variant='contained' color='error'>Odmowa</LoadingButton>
    </Paper>
}